import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogIn, UserPlus, LogOut, RefreshCw, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useSub, fetchReceipts } from "@/lib/subscription";
import { ReceiptCard } from "@/components/UpgradeGate";

export default function AccountCard() {
  const { user, ready, login, register, logout } = useAuth();
  const { sub, premium, refresh } = useSub();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [receipts, setReceipts] = useState([]);

  useEffect(() => { if (user) fetchReceipts().then(setReceipts).catch(() => {}); else setReceipts([]); }, [user, sub]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (mode === "login") await login(form.email.trim(), form.password);
      else await register(form.email.trim(), form.password, form.name.trim());
      const s = await refresh();
      toast.success(s?.status === "active" ? "Welcome back! Your Premium plan is restored on this device. 🌟" : "You're signed in.");
    } catch (err) { setError(err.message || "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  const doLogout = () => { logout(); toast.success("Signed out."); };
  const endsAt = sub?.ends_at ? new Date(sub.ends_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : null;

  if (!ready) return null;

  if (user) {
    return (
      <div className="ll-card p-5" data-testid="account-card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 text-white flex items-center justify-center"><ShieldCheck size={26} /></div>
          <div className="flex-1 min-w-[180px]">
            <div className="text-purple-500 uppercase tracking-widest text-xs font-bold">Parent account</div>
            <div className="font-display font-bold text-xl text-purple-900" data-testid="account-email">{user.email}</div>
            <div className={`text-sm font-bold mt-0.5 ${premium ? "text-emerald-600" : "text-purple-500"}`} data-testid="account-plan">
              {premium ? `${sub.plan} plan · active until ${endsAt}` : "Free plan"}
            </div>
          </div>
          <div className="flex gap-2">
            <button data-testid="account-refresh-button" onClick={() => refresh().then(() => toast.success("Subscription synced."))} className="ll-btn ll-btn-secondary"><RefreshCw size={16} /> Restore</button>
            <button data-testid="account-logout-button" onClick={doLogout} className="ll-btn ll-btn-secondary"><LogOut size={16} /> Sign out</button>
          </div>
        </div>
        {receipts.length > 0 && (
          <div className="mt-5">
            <div className="font-display font-bold text-lg text-purple-900 mb-2">Payment history</div>
            <div className="space-y-3">{receipts.map((r) => <ReceiptCard key={r.receipt_id} receipt={r} testId={`history-receipt-${r.receipt_id}`} />)}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="ll-card p-5" data-testid="account-card">
      <div className="text-purple-500 uppercase tracking-widest text-xs font-bold">Parent account</div>
      <h3 className="font-display font-bold text-xl text-purple-900 mt-1">{mode === "login" ? "Log in to sync your subscription" : "Create a parent account"}</h3>
      <p className="text-purple-700/70 font-semibold text-sm mt-1">Your Premium plan follows your email — use it on any device.</p>
      <form onSubmit={submit} className="mt-4 space-y-3">
        {mode === "register" && (
          <input data-testid="account-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name (optional)" className="ll-input w-full" />
        )}
        <input data-testid="account-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="ll-input w-full" />
        <input data-testid="account-password-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (min 6 characters)" className="ll-input w-full" />
        {error && <div className="text-pink-600 font-bold text-sm" data-testid="account-error">{error}</div>}
        <button data-testid="account-submit-button" disabled={busy} className="ll-btn ll-btn-primary w-full">
          {mode === "login" ? <><LogIn size={18} /> {busy ? "Logging in…" : "Log in"}</> : <><UserPlus size={18} /> {busy ? "Creating…" : "Create account"}</>}
        </button>
      </form>
      <button data-testid="account-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(null); }} className="mt-3 text-purple-600 font-bold text-sm underline w-full text-center">
        {mode === "login" ? "New here? Create a parent account" : "Already have an account? Log in"}
      </button>
    </div>
  );
}
